import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import { Building2, Users, Bot, MessageSquare, HardDrive } from 'lucide-react'
import { useTenantUsage } from '../hooks/useTenantUsage'

interface TenantUsageTabProps {
  tenantId: string
}

interface UsageItem {
  current: number
  limit: number
}

export function TenantUsageTab({ tenantId }: TenantUsageTabProps) {
  const { data: usage, isLoading, error } = useTenantUsage(tenantId)

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-2 w-full mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-10 text-destructive">
        Failed to load usage data
      </div>
    )
  }

  if (!usage) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        No usage data available
      </div>
    )
  }

  const getUsagePercentage = (item: UsageItem) => {
    if (!item.limit || item.limit === 0) return 0
    return Math.min((item.current / item.limit) * 100, 100)
  }

  const getLimitText = (item: UsageItem) => {
    if (!item.limit || item.limit === 0) return 'Unlimited'
    return `/ ${item.limit}`
  }

  const usageCards = [
    {
      title: 'Organizations',
      data: usage.organizations as UsageItem,
      icon: Building2,
      description: 'Organizations in this tenant',
    },
    {
      title: 'Users',
      data: usage.users as UsageItem,
      icon: Users,
      description: 'Total users across all organizations',
    },
    {
      title: 'Agents',
      data: usage.agents as UsageItem,
      icon: Bot,
      description: 'Total AI agents created',
    },
    {
      title: 'Prompts',
      data: usage.prompts as UsageItem,
      icon: MessageSquare,
      description: 'Total prompts stored',
    },
    {
      title: 'Storage',
      data: usage.storage as UsageItem,
      icon: HardDrive,
      description: 'Storage used (GB)',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {usageCards.map((card) => {
          const percentage = getUsagePercentage(card.data)
          const isNearLimit = percentage > 80

          return (
            <Card key={card.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
                <card.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {card.data?.current || 0}
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    {getLimitText(card.data)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{card.description}</p>
                {card.data?.limit > 0 && (
                  <Progress
                    value={percentage}
                    className={`h-2 ${isNearLimit ? '[&>div]:bg-orange-500' : ''}`}
                  />
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Quota Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Quota Summary</CardTitle>
          <CardDescription>
            Resource usage overview for this tenant
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {usageCards.map((card) => {
              const percentage = getUsagePercentage(card.data)
              const isNearLimit = percentage > 80
              const isAtLimit = percentage >= 100

              return (
                <div key={card.title} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{card.title}</span>
                    <span className={
                      isAtLimit ? 'text-destructive' :
                      isNearLimit ? 'text-orange-500' :
                      'text-muted-foreground'
                    }>
                      {card.data?.current || 0} {getLimitText(card.data)}
                    </span>
                  </div>
                  {card.data?.limit > 0 && (
                    <Progress
                      value={percentage}
                      className={`h-1.5 ${
                        isAtLimit ? '[&>div]:bg-destructive' :
                        isNearLimit ? '[&>div]:bg-orange-500' : ''
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
